import { evalCond } from './story/chainTree';
import type { CondDef } from './story/types';
import {
  ACTOR_STATES,
  OBJECTIVE_STATES,
  SERVER_ACTIONS,
  condFacts,
  targetActor,
  type ObjectiveDef,
  type ScenarioActionDef,
  type ScenarioDef,
  type ScenarioEventDef,
  type ScenarioTriggerDef,
} from './validate/scenarios';

/**
 * A browser dry run of shared-cs `ScenarioRules` (Begin, Step per logged event, Finish) for the scenario editor: authors script a
 * list of log events and see which scenario triggers fire, which objectives tick and the verdict. **The server's shared-cs
 * `ScenarioRules` (replayed by `ScenarioVerifier`) is authoritative**; this mirror skips what needs a generated layout (room
 * indexes, the found-too-soon and escort-too-fast distance bounds, `MinMsToRoom`), so `enter` takes a room name (a set piece id, a
 * room template, or `room:N`) instead of an index. Everything else follows the C#: timers resolve from event times (in due order,
 * before the event that passes them), triggers cascade up to 64 events, and win and lose are checked after every event.
 */

/** One scripted log entry: `ev` is a `ScenarioLogEvents` name, or `end` (`arg` = extracted | died) to finish the raid. */
export type DryRunEvent = { atS: number; ev: string; arg?: string };

export type DryRunStep = {
  atS: number;
  /** What happened: the event, or a timer firing. */
  label: string;
  fired: string[];
  /** Objective changes as `id: state`. */
  objectives: string[];
  /** Client actions performed, and server actions queued (marked "server:"). */
  actions: string[];
  /** Implausible or malformed notes this step. */
  notes: string[];
  /** The verdict after this step ('' while undecided). */
  verdict: string;
};

export type DryRunResult = {
  steps: DryRunStep[];
  verdict: '' | 'won' | 'lost';
  objectives: Record<string, string>;
  actors: Record<string, { health: number; state: string }>;
  implausible: string[];
  malformed: string[];
  /** The server actions that apply after the verdict (fired triggers', then onWin or onLose), from content only. */
  serverActions: string[];
  finished: boolean;
};

export const DRY_RUN_EVENTS = [
  'enter',
  'found',
  'pick_up',
  'door',
  'interact',
  'free_start',
  'free_done',
  'free_cancel',
  'wave_spawned',
  'wave_cleared',
  'actor_health',
  'actor_dead',
  'escort_start',
  'reached',
  'innocent_killed',
  'horde_level',
  'building_clear',
  'beat_outcome',
  'end',
] as const;

/** What `arg` holds for each event (shown as a placeholder in the editor). */
export const DRY_RUN_ARG_HINT: Record<string, string> = {
  enter: 'room: set piece id, template or room:N',
  found: 'actor:id, item:id or prop:id',
  pick_up: 'item:id',
  door: 'door tag',
  interact: 'prop:id',
  free_start: 'target (door:chained)',
  free_done: 'target (door:chained)',
  free_cancel: 'target',
  wave_spawned: 'wave n',
  wave_cleared: 'wave n',
  actor_health: 'actor:health (okafor:25)',
  actor_dead: 'actor id',
  escort_start: 'actor id',
  reached: 'actor@mark (okafor@exit)',
  innocent_killed: 'actor id',
  horde_level: 'percent',
  building_clear: '',
  beat_outcome: 'beatId:outcomeId',
  end: 'extracted or died',
};

const LOSE_TIMER = '__lose';
const HOLD_TIMER = '__hold.';
const MAX_CASCADE = 64;

type Ev = {
  type: string;
  room?: string;
  id?: string;
  state?: string;
  objective?: string;
  actor?: string;
  before?: number;
  after?: number;
};
type Timer = { dueMs: number; fired: boolean };
type Actor = { health: number; max: number; state: string };

const ms = (s: number | undefined) => Math.round((s ?? 0) * 1000);
const alive = (a: Actor) => a.health > 0 && a.state !== 'dead' && a.state !== 'turned';

/** `ScenarioConditions.Eval`'s defaults (no op = `eq`, and no op nor value = `eq "true"`), so `evalCond` reads it the same way. */
function normalize(c: CondDef | null | undefined): CondDef | null | undefined {
  if (c == null) return c;
  const op = c.op || 'eq';
  const value = !c.op && !c.value ? 'true' : (c.value ?? '');
  return {
    ...c,
    op: c.fact ? op : c.op,
    value: c.fact ? value : c.value,
    all: (c.all ?? []).map((x) => normalize(x)!),
    any: (c.any ?? []).map((x) => normalize(x)!),
    not: normalize(c.not) ?? null,
  };
}

export function dryRun(
  scenario: ScenarioDef,
  script: DryRunEvent[],
  opts: { vars?: Record<string, string> } = {},
): DryRunResult {
  const vars = opts.vars ?? {};
  const objectivesList = (scenario.objectives ?? []).filter(
    (o): o is ObjectiveDef => o != null && !!o.id,
  );
  const triggers = (scenario.triggers ?? []).filter((t): t is ScenarioTriggerDef => t != null);
  const lose = (scenario.lose ?? []).filter((l): l is ScenarioEventDef => l != null);

  const objectives: Record<string, string> = {};
  const startedAt: Record<string, number> = {};
  const wavesCleared: Record<string, number> = {};
  const timers: Record<string, Timer> = {};
  const actors: Record<string, Actor> = {};
  const fired: string[] = [];
  const entered = new Set<string>();
  const found = new Set<string>();
  const implausible: string[] = [];
  const malformed: string[] = [];
  const serverActions: string[] = [];
  let innocents = 0;
  let hordePct = 0;
  let atMs = 0;
  let verdict: '' | 'won' | 'lost' = '';
  const steps: DryRunStep[] = [];
  let cur: DryRunStep = {
    atS: 0,
    label: '',
    fired: [],
    objectives: [],
    actions: [],
    notes: [],
    verdict: '',
  };

  const open = (label: string, at: number) => {
    cur = { atS: at / 1000, label, fired: [], objectives: [], actions: [], notes: [], verdict: '' };
    steps.push(cur);
  };
  const close = () => {
    cur.verdict = verdict;
  };
  const stateOf = (id: string) => objectives[id] ?? 'open';
  const available = (o: ObjectiveDef) => (o.after ?? []).every((a) => stateOf(a) === 'done');
  const setVerdict = (v: 'won' | 'lost') => {
    if (!verdict) verdict = v;
  };
  const setObjective = (q: Ev[], id: string, state: string, at: number) => {
    if (stateOf(id) === state) return;
    objectives[id] = state;
    if (state === 'started') startedAt[id] = at;
    cur.objectives.push(`${id}: ${state}`);
    q.push({ type: 'objective', objective: id, state });
  };
  const completeNoKills = () => {
    for (const o of objectivesList)
      if (o.type === 'no_kill' && stateOf(o.id!) !== 'failed') objectives[o.id!] = 'done';
  };
  const completeFinds = (q: Ev[], target: string, at: number) => {
    for (const o of objectivesList)
      if (o.type === 'find' && o.target === target && stateOf(o.id!) === 'open' && available(o))
        setObjective(q, o.id!, 'done', at);
  };

  const fact = (f: string): string => {
    if (f.startsWith('objective.')) {
      const id = f.slice(10);
      return id in objectives ? stateOf(id) : '';
    }
    if (f.startsWith('actor.')) {
      const rest = f.slice(6);
      const health = rest.endsWith('.health');
      const a = actors[health ? rest.slice(0, -7) : rest];
      if (!a) return '';
      return health ? String(a.health) : a.state;
    }
    if (f.startsWith('found.')) return found.has(f.slice(6)) ? 'true' : 'false';
    if (f.startsWith('timer.')) {
      const t = timers[f.slice(6)];
      return t ? (t.fired ? 'fired' : 'running') : '';
    }
    if (f === 'raid.innocents_killed') return String(innocents);
    return '';
  };
  const holds = (c: CondDef | null | undefined) => {
    if (c == null) return true;
    const context: Record<string, string> = {};
    const v: Record<string, string> = { ...vars };
    for (const f of condFacts(c)) {
      if (f.startsWith('var.')) v[f.slice(4)] ??= '';
      else context[f] = fact(f);
    }
    return evalCond(normalize(c), v, context);
  };

  const matches = (on: ScenarioEventDef, ev: Ev) => {
    if (on.type !== ev.type) return false;
    switch (ev.type) {
      case 'enter_room':
        return !on.room || on.room === ev.room;
      case 'actor_health':
        return (
          (!on.actor || on.actor === ev.actor) &&
          (ev.before ?? 0) >= (on.belowPct ?? 0) &&
          (ev.after ?? 0) < (on.belowPct ?? 0)
        );
      case 'actor_dead':
        return !on.actor || on.actor === ev.actor;
      case 'reached':
        return (!on.actor || on.actor === ev.actor) && (!on.id || on.id === ev.id);
      case 'objective':
        return (
          (!on.objective || on.objective === ev.objective) && (!on.state || on.state === ev.state)
        );
      case 'horde_level':
        return (ev.before ?? 0) < (on.belowPct ?? 0) && (ev.after ?? 0) >= (on.belowPct ?? 0);
      case 'beat_outcome':
      case 'var':
        return (!on.id || on.id === ev.id) && (!on.state || on.state === ev.state);
      case 'start':
      case 'building_clear':
        return true;
      default:
        return !on.id || on.id === ev.id;
    }
  };

  const describe = (a: ScenarioActionDef) => {
    switch (a.type) {
      case 'beat':
        return `beat ${a.beatId ?? ''}`;
      case 'caption':
      case 'bark':
        return `${a.type}${a.actor ? ` ${a.actor}` : ''}: "${a.text ?? ''}"`;
      case 'timer':
        return `timer ${a.id ?? ''} ${a.s ?? 0} s${a.visible === false ? ' (hidden)' : ''}`;
      case 'actor':
        return `actor ${a.actor ?? ''} → ${a.set ?? ''}`;
      case 'set_flag':
        return `set_flag ${a.key ?? ''}=${a.value ?? ''}`;
      case 'recruit':
        return `recruit ${a.npc ?? ''}`;
      case 'effect':
        return `effect ${a.effect?.type ?? ''}`;
      default:
        return [a.type, a.id, a.set, a.key, a.value].filter(Boolean).join(' ');
    }
  };

  const perform = (q: Ev[], t: ScenarioTriggerDef, at: number) => {
    (t.do ?? []).forEach((a, i) => {
      if (a == null || verdict) return;
      switch (a.type) {
        case 'objective':
          if (!a.id || !(a.id in objectives)) break;
          if (a.set === 'revealed') cur.objectives.push(`${a.id}: revealed`);
          else if ((OBJECTIVE_STATES as readonly string[]).includes(a.set ?? ''))
            setObjective(q, a.id, a.set!, at);
          break;
        case 'actor': {
          const actor = actors[a.actor ?? ''];
          if (actor && alive(actor) && (ACTOR_STATES as readonly string[]).includes(a.set ?? '')) {
            actor.state = a.set!;
            if (a.set === 'dead') actor.health = 0;
          }
          cur.actions.push(describe(a));
          break;
        }
        case 'timer':
          if (a.id && !a.id.startsWith('__')) timers[a.id] = { dueMs: at + ms(a.s), fired: false };
          cur.actions.push(describe(a));
          break;
        case 'win':
          setVerdict('won');
          completeNoKills();
          cur.actions.push('win');
          break;
        case 'lose':
          setVerdict('lost');
          cur.actions.push('lose');
          break;
        default:
          if ((SERVER_ACTIONS as readonly string[]).includes(a.type ?? '')) {
            serverActions.push(`${t.id ?? ''}#${i}: ${describe(a)}`);
            cur.actions.push(`server: ${describe(a)}`);
          } else cur.actions.push(describe(a));
      }
    });
  };

  const checkWin = () => {
    if (verdict) return;
    let any = false;
    let all = true;
    for (const o of objectivesList) {
      if (o.required === false) continue;
      const st = stateOf(o.id!);
      if (st === 'failed') {
        setVerdict('lost');
        return;
      }
      if (o.type === 'no_kill') continue;
      any = true;
      if (st !== 'done') all = false;
    }
    if (any && all) {
      setVerdict('won');
      completeNoKills();
    }
  };

  const autoStart = (q: Ev[], at: number) => {
    for (const o of objectivesList) {
      if (o.type !== 'hold' || stateOf(o.id!) !== 'open' || !available(o)) continue;
      setObjective(q, o.id!, 'started', at);
      if ((o.waves ?? 0) <= 0 && (o.holdS ?? 0) > 0)
        timers[HOLD_TIMER + o.id] = { dueMs: at + ms(o.holdS), fired: false };
    }
  };

  const drain = (q: Ev[], at: number) => {
    let processed = 0;
    while (q.length > 0 && processed++ < MAX_CASCADE) {
      const ev = q.shift()!;
      if (verdict) break;
      if (ev.type === 'objective')
        for (const l of lose)
          if (
            l.type === 'objective' &&
            l.objective === ev.objective &&
            (!l.state || l.state === ev.state)
          )
            setVerdict('lost');
      for (const t of triggers) {
        if (!t.on || !matches(t.on, ev)) continue;
        if (t.once && fired.includes(t.id ?? '')) continue;
        if (t.if != null && !holds(t.if as CondDef)) continue;
        if (!fired.includes(t.id ?? '')) fired.push(t.id ?? '');
        cur.fired.push(t.id ?? '');
        perform(q, t, at);
        if (verdict) break;
      }
      autoStart(q, at);
      checkWin();
    }
    checkWin();
  };

  const fireTimers = (upTo: number) => {
    for (let guard = 0; guard < MAX_CASCADE; guard++) {
      let next: string | null = null;
      let due = Number.MAX_SAFE_INTEGER;
      for (const [id, t] of Object.entries(timers)) {
        if (t.fired || t.dueMs > upTo) continue;
        if (t.dueMs < due || (t.dueMs === due && next !== null && id < next)) {
          next = id;
          due = t.dueMs;
        }
      }
      if (next === null) return;
      timers[next]!.fired = true;
      if (verdict) continue;
      if (due > atMs) atMs = due;
      open(
        next === LOSE_TIMER
          ? 'lose timer runs out'
          : next.startsWith(HOLD_TIMER)
            ? `hold '${next.slice(HOLD_TIMER.length)}' timer done`
            : `timer '${next}' fires`,
        due,
      );
      const q: Ev[] = [];
      if (next === LOSE_TIMER) setVerdict('lost');
      else {
        if (next.startsWith(HOLD_TIMER)) {
          const id = next.slice(HOLD_TIMER.length);
          if (stateOf(id) === 'started') setObjective(q, id, 'done', due);
        } else q.push({ type: 'timer', id: next });
        autoStart(q, due);
        drain(q, due);
      }
      close();
    }
  };

  // Begin: objectives open, actors at full health, the lose timer, then start and the var triggers.
  for (const o of objectivesList) objectives[o.id!] = 'open';
  for (const a of scenario.actors ?? []) {
    if (a == null || !a.id) continue;
    const hp = (a.health ?? 100) > 0 ? (a.health ?? 100) : 100;
    actors[a.id] = { health: hp, max: hp, state: 'idle' };
  }
  for (const l of lose)
    if (l.type === 'timer' && (l.s ?? 0) > 0) timers[LOSE_TIMER] = { dueMs: ms(l.s), fired: false };
  open('start', 0);
  {
    const q: Ev[] = [{ type: 'start' }];
    for (const t of triggers)
      if (
        t.on?.type === 'var' &&
        t.on.id &&
        t.on.id in vars &&
        (!t.on.state || vars[t.on.id] === t.on.state)
      )
        q.push({ type: 'var', id: t.on.id, state: vars[t.on.id] });
    autoStart(q, 0);
    drain(q, 0);
  }
  close();

  let finished = false;
  for (const e of script) {
    if (finished) break;
    const at = ms(e.atS);
    const arg = e.arg ?? '';
    if (at < atMs || at < 0) {
      malformed.push(`time_backwards@${at}`);
      open(`${e.ev} ${arg}: time goes backwards (ignored)`, at);
      cur.notes.push(`time_backwards@${at}`);
      close();
      continue;
    }
    if (e.ev === 'end') {
      fireTimers(at);
      atMs = at;
      open(`raid ends: ${arg || 'extracted'}`, at);
      if (!verdict && (arg || 'extracted') === 'extracted') {
        const q: Ev[] = [];
        for (const o of objectivesList)
          if (o.type === 'reach_exit' && stateOf(o.id!) !== 'done' && available(o))
            setObjective(q, o.id!, 'done', at);
        drain(q, at);
      }
      if (!verdict) setVerdict('lost');
      close();
      finished = true;
      break;
    }
    if (!(DRY_RUN_EVENTS as readonly string[]).includes(e.ev)) {
      malformed.push(`unknown_event:${e.ev}`);
      open(`${e.ev}: unknown event (ignored)`, at);
      cur.notes.push(`unknown_event:${e.ev}`);
      close();
      continue;
    }
    fireTimers(at);
    atMs = at;
    open(`${e.ev}${arg ? ` ${arg}` : ''}`, at);
    if (verdict) {
      cur.notes.push('after the verdict: changes nothing');
      close();
      continue;
    }
    const q: Ev[] = [];
    const bad = (why: string) => {
      malformed.push(why);
      cur.notes.push(`malformed: ${why}`);
    };
    const flag = (why: string) => {
      implausible.push(why);
      cur.notes.push(`implausible: ${why}`);
    };
    let skip = false;
    switch (e.ev) {
      case 'enter':
        if (!arg) {
          bad(`unknown_room:${arg}`);
          skip = true;
        } else if (!entered.has(arg)) {
          entered.add(arg);
          q.push({ type: 'enter_room', room: arg });
        }
        break;
      case 'found': {
        const known = arg.startsWith('actor:')
          ? arg.slice(6) in actors
          : ['item:', 'prop:', 'door:', 'anchor:'].some((p) => arg.startsWith(p));
        if (!known) {
          bad(arg.startsWith('actor:') ? `unknown_actor:${arg.slice(6)}` : `unknown_target:${arg}`);
          skip = true;
          break;
        }
        found.add(arg);
        completeFinds(q, arg, at);
        break;
      }
      case 'pick_up':
        found.add(arg);
        completeFinds(q, arg, at);
        q.push({ type: 'pick_up', id: arg });
        break;
      case 'door':
        q.push({ type: 'door', id: arg });
        break;
      case 'interact':
        for (const o of objectivesList)
          if (
            o.type === 'crescendo' &&
            o.target === arg &&
            stateOf(o.id!) === 'open' &&
            available(o)
          )
            setObjective(q, o.id!, 'started', at);
        for (const l of lose) if (l.type === 'interact' && l.id === arg) setVerdict('lost');
        q.push({ type: 'interact', id: arg });
        break;
      case 'free_start':
        for (const o of objectivesList)
          if (o.type === 'free' && o.target === arg && stateOf(o.id!) === 'open' && available(o))
            setObjective(q, o.id!, 'started', at);
        break;
      case 'free_cancel':
        for (const o of objectivesList)
          if (o.type === 'free' && o.target === arg && stateOf(o.id!) === 'started') {
            objectives[o.id!] = 'open';
            delete startedAt[o.id!];
            cur.objectives.push(`${o.id}: open`);
          }
        break;
      case 'free_done':
        for (const o of objectivesList) {
          if (o.type !== 'free' || o.target !== arg || stateOf(o.id!) !== 'started') continue;
          const started = startedAt[o.id!] ?? at;
          if (at - started < ms(o.holdS) - 500) flag(`free_too_fast:${o.id}`);
          setObjective(q, o.id!, 'done', at);
        }
        break;
      case 'wave_spawned':
        q.push({ type: 'wave_spawned', id: arg });
        break;
      case 'wave_cleared':
        for (const o of objectivesList) {
          if ((o.type !== 'hold' && o.type !== 'crescendo') || (o.waves ?? 0) <= 0) continue;
          if (stateOf(o.id!) !== 'started') continue;
          const n = (wavesCleared[o.id!] ?? 0) + 1;
          wavesCleared[o.id!] = n;
          cur.notes.push(`${o.id}: wave ${n} of ${o.waves}`);
          if (n >= (o.waves ?? 0)) setObjective(q, o.id!, 'done', at);
        }
        q.push({ type: 'wave_cleared', id: arg });
        break;
      case 'actor_health': {
        const colon = arg.lastIndexOf(':');
        const id = colon > 0 ? arg.slice(0, colon) : '';
        const a = actors[id];
        const hp = Number(arg.slice(colon + 1));
        if (colon <= 0 || !a || !Number.isInteger(hp)) {
          bad(`bad_actor_health:${arg}`);
          skip = true;
          break;
        }
        if (hp < 0 || hp > a.max) flag(`actor_health_out_of_range:${id}`);
        const before = a.max > 0 ? Math.trunc((a.health * 100) / a.max) : 0;
        a.health = Math.max(0, Math.min(a.max, hp));
        const after = a.max > 0 ? Math.trunc((a.health * 100) / a.max) : 0;
        q.push({ type: 'actor_health', actor: id, before, after });
        break;
      }
      case 'actor_dead': {
        const a = actors[arg];
        if (!a) {
          bad(`unknown_actor:${arg}`);
          skip = true;
          break;
        }
        a.health = 0;
        a.state = 'dead';
        for (const o of objectivesList) {
          const os = stateOf(o.id!);
          if (os === 'done' || os === 'failed' || targetActor(o) !== arg) continue;
          if (o.type === 'kill_target') {
            if (!found.has(`actor:${arg}`)) flag(`killed_before_found:${arg}`);
            setObjective(q, o.id!, 'done', at);
          } else if (o.type === 'escort') setObjective(q, o.id!, 'failed', at);
        }
        for (const l of lose)
          if (l.type === 'actor_dead' && (!l.actor || l.actor === arg)) setVerdict('lost');
        q.push({ type: 'actor_dead', actor: arg });
        break;
      }
      case 'escort_start':
        if (!(arg in actors)) {
          bad(`unknown_actor:${arg}`);
          skip = true;
          break;
        }
        for (const o of objectivesList)
          if (
            o.type === 'escort' &&
            targetActor(o) === arg &&
            stateOf(o.id!) === 'open' &&
            available(o)
          ) {
            setObjective(q, o.id!, 'started', at);
            if (alive(actors[arg]!)) actors[arg]!.state = 'following';
          }
        break;
      case 'reached': {
        const sign = arg.indexOf('@');
        const id = sign > 0 ? arg.slice(0, sign) : arg;
        const mark = sign > 0 ? arg.slice(sign + 1) : 'exit';
        const a = actors[id];
        if (!a) {
          bad(`unknown_actor:${id}`);
          skip = true;
          break;
        }
        for (const o of objectivesList) {
          if (o.type !== 'escort' || targetActor(o) !== id || stateOf(o.id!) !== 'started')
            continue;
          if ((o.to || 'exit') !== mark) continue;
          if (!alive(a)) {
            flag(`escorted_dead:${id}`);
            continue;
          }
          setObjective(q, o.id!, 'done', at);
        }
        q.push({ type: 'reached', actor: id, id: mark });
        break;
      }
      case 'innocent_killed':
        innocents++;
        if (actors[arg]) {
          actors[arg].health = 0;
          actors[arg].state = 'dead';
        }
        for (const o of objectivesList)
          if (o.type === 'no_kill' && stateOf(o.id!) !== 'failed')
            setObjective(q, o.id!, 'failed', at);
        if (arg in actors) q.push({ type: 'actor_dead', actor: arg });
        break;
      case 'horde_level': {
        const p = Number.parseInt(arg, 10);
        const pct = Number.isFinite(p) ? Math.max(0, Math.min(100, p)) : 0;
        q.push({ type: 'horde_level', before: hordePct, after: pct });
        hordePct = pct;
        break;
      }
      case 'building_clear':
        q.push({ type: 'building_clear' });
        break;
      case 'beat_outcome': {
        const colon = arg.indexOf(':');
        q.push({
          type: 'beat_outcome',
          id: colon > 0 ? arg.slice(0, colon) : arg,
          state: colon > 0 ? arg.slice(colon + 1) : '',
        });
        break;
      }
    }
    if (!skip) {
      autoStart(q, at);
      drain(q, at);
    }
    close();
  }

  if (finished && verdict) {
    const after = verdict === 'won' ? scenario.onWin : scenario.onLose;
    (after ?? []).forEach((a, i) => {
      if (a) serverActions.push(`${verdict === 'won' ? 'onWin' : 'onLose'}#${i}: ${describe(a)}`);
    });
  }

  return {
    steps,
    verdict,
    objectives: { ...objectives },
    actors: Object.fromEntries(
      Object.entries(actors).map(([k, a]) => [k, { health: a.health, state: a.state }]),
    ),
    implausible,
    malformed,
    serverActions,
    finished,
  };
}

/**
 * A plausible script for the editor to start from: enter the first set piece, then each required objective's events in an order
 * that respects `after`, ~10 s apart, and extract.
 */
export function suggestScript(scenario: ScenarioDef): DryRunEvent[] {
  const out: DryRunEvent[] = [];
  let t = 5;
  const push = (ev: string, arg = '', gap = 10) => {
    out.push({ atS: Math.round(t * 10) / 10, ev, arg });
    t += gap;
  };
  const sp = (scenario.setPieces ?? []).find((r) => r?.id)?.id;
  if (sp) push('enter', sp);
  const list = (scenario.objectives ?? []).filter(
    (o): o is ObjectiveDef => o != null && !!o.id && o.required !== false,
  );
  const done = new Set<string>();
  const ids = new Set(list.map((o) => o.id!));
  for (let pass = 0; pass < list.length && done.size < list.length; pass++)
    for (const o of list) {
      if (done.has(o.id!) || !(o.after ?? []).every((a) => done.has(a) || !ids.has(a))) continue;
      done.add(o.id!);
      const actor = targetActor(o);
      switch (o.type) {
        case 'find':
          push('found', o.target ?? '');
          break;
        case 'free':
          push('free_start', o.target ?? '', Math.max(1, o.holdS ?? 0) + 0.5);
          push('free_done', o.target ?? '');
          break;
        case 'hold':
        case 'crescendo':
          if (o.type === 'crescendo') push('interact', o.target ?? '');
          if ((o.waves ?? 0) > 0)
            for (let n = 1; n <= (o.waves ?? 0); n++) {
              push('wave_spawned', String(n), 15);
              push('wave_cleared', String(n), 5);
            }
          else t += (o.holdS ?? 0) + 1;
          break;
        case 'escort':
          push('escort_start', actor, 30);
          push('reached', `${actor}@${o.to || 'exit'}`);
          break;
        case 'kill_target':
          push('found', `actor:${actor}`);
          push('actor_dead', actor);
          break;
      }
    }
  push('end', 'extracted');
  return out;
}
